import Dexie, {} from 'dexie';
import { db } from './db.js';
import { repository } from './repository.js';
import { assertBackupAttachmentSize, attachmentFromBackup, attachmentToBackup } from './attachments.js';
import { assertImportReferences, rowReferences, validImportRow } from './import-integrity.js';
export const DATA_TABLES = [
    'pets', 'treatments', 'treatmentLogs', 'checkins', 'vitals', 'events', 'visits', 'qolRecords', 'thresholds',
    'coughLogs', 'meals', 'gaitLogs', 'labs', 'routines', 'routineLogs', 'costs', 'handoffs',
    'carePlans', 'careQuestions', 'careEventGroups', 'careSupplies', 'carePassports', 'careEpisodes',
    'eliminations', 'waterLogs', 'mealPresets', 'dayMarks', 'photoDays',
];
export async function exportSnapshot(options = {}) {
    const raw = await db.transaction('r', storageTables.map(targetTable), readRawState);
    assertBackupAttachmentSize(raw.attachments);
    const snapshot = {
        schemaVersion: 1,
        exportedAt: Date.now(),
        tables: Object.fromEntries(DATA_TABLES.map((table) => [table, raw[table]])),
        attachments: await Promise.all(raw.attachments.map(attachmentToBackup)),
        trash: raw.trash,
    };
    return options.petId ? snapshotForPet(snapshot, options.petId) : snapshot;
}
export function belongsToPet(table, row, petId) { return table === 'pets' ? row.id === petId : row.petId === petId; }
export function snapshotForPet(snapshot, petId) {
    const tables = Object.fromEntries(Object.entries(snapshot.tables).map(([table, rows]) => [table, (rows ?? []).filter(row => belongsToPet(table, row, petId))]));
    const ids = new Set();
    for (const rows of Object.values(tables))
        for (const row of rows ?? []) {
            for (const id of row.attachmentIds ?? [])
                ids.add(id);
            const photo = row.photoBlobId;
            if (photo)
                ids.add(photo);
        }
    return { ...snapshot, tables, attachments: snapshot.attachments?.filter(a => a.petId === petId && ids.has(a.id)), trash: snapshot.trash?.filter(t => belongsToPet(t.table, t.snapshot, petId)) };
}
function kstDay(epoch) {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Seoul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(epoch));
}
/** Keep one device-local snapshot per KST day and retain the latest three. */
export async function createDailyBackupIfDue(now = Date.now()) {
    const latest = await db.localBackups.orderBy('createdAt').reverse().first();
    if (latest && kstDay(latest.createdAt) === kstDay(now))
        return false;
    const snapshot = await exportSnapshot();
    await db.localBackups.put({ id: `daily-${kstDay(now)}`, kind: 'daily', createdAt: now, snapshot });
    const rows = await db.localBackups.orderBy('createdAt').reverse().toArray();
    const stale = rows.slice(3).map((row) => row.id);
    if (stale.length)
        await db.localBackups.bulkDelete(stale);
    return true;
}
/** Explicit migration hook: callers invoke this before changing the Dexie schema. */
export async function createMigrationBackup(now = Date.now()) {
    const id = `migration-${now}`;
    await db.migrationBackups.put({ id, createdAt: now, snapshot: await exportSnapshot() });
    const rows = await db.migrationBackups.orderBy('createdAt').reverse().toArray();
    const stale = rows.slice(1).map((row) => row.id);
    if (stale.length)
        await db.migrationBackups.bulkDelete(stale);
    return id;
}
export async function listDailyBackups() {
    return db.localBackups.orderBy('createdAt').reverse().toArray();
}
export async function getDailyBackupStatus() {
    const rows = await db.localBackups.orderBy('createdAt').reverse().toArray();
    return { count: rows.length, latestAt: rows[0]?.createdAt };
}
/** Permanently clear every product-owned IndexedDB table, including recovery
 * snapshots and sync metadata. Callers must obtain an explicit two-step user
 * confirmation before invoking this irreversible operation. */
export async function deleteAllLocalData() {
    await db.transaction('rw', db.tables, async () => {
        for (const table of db.tables)
            await table.clear();
    });
}
export async function getLatestMigrationBackup() {
    return db.migrationBackups.orderBy('createdAt').reverse().first();
}
const emptyResult = () => ({ imported: 0, skipped: 0, unchanged: 0, conflicts: 0, failed: 0, errors: [] });
const referenceTables = [...DATA_TABLES, 'attachments'];
const storageTables = [...referenceTables, 'trash'];
const targetTable = (table) => db[table];
async function readRawState() {
    const rows = {};
    for (const table of storageTables)
        rows[table] = await targetTable(table).toArray();
    return rows;
}
async function rowKey(row) {
    if (!row)
        return 'null';
    const value = 'blob' in row ? await attachmentToBackup(row) : row;
    return JSON.stringify(value, (_key, item) => item && typeof item === 'object' && !Array.isArray(item)
        ? Object.fromEntries(Object.entries(item).sort(([left], [right]) => left.localeCompare(right))) : item);
}
// Blob bytes are part of the comparison. Keep only this bounded local conversion
// alive in IDB; network/provider operations never run inside the transaction.
async function equalRow(a, b) {
    return Dexie.waitFor(Promise.all([rowKey(a), rowKey(b)]).then(([left, right]) => left === right));
}
async function stateKey(rows) {
    return Dexie.waitFor(Promise.all(storageTables.map(async (table) => [table, await Promise.all([...rows[table]].sort((a, b) => a.id.localeCompare(b.id)).map(rowKey))])).then(JSON.stringify));
}
function validBase(value) {
    if (!value || typeof value !== 'object')
        return false;
    const row = value;
    return typeof row.id === 'string' && row.id.length > 0 && Number.isFinite(row.createdAt) && Number.isFinite(row.updatedAt);
}
function validTrash(value) {
    if (!validBase(value))
        return false;
    const row = value;
    if (!DATA_TABLES.includes(row.table) || !Number.isFinite(row.deletedAt)
        || (row.deletedRevision != null && !Number.isFinite(row.deletedRevision))
        || !row.snapshot || typeof row.snapshot !== 'object' || Array.isArray(row.snapshot))
        return false;
    return validBase(row.snapshot) && validImportRow(row.table, row.snapshot);
}
function referenceState(rows) {
    return Object.fromEntries(referenceTables.map((table) => [table, rows[table]]));
}
async function prepareSnapshot(snapshot) {
    if (!snapshot || snapshot.schemaVersion !== 1 || !snapshot.tables || typeof snapshot.tables !== 'object' || Array.isArray(snapshot.tables))
        throw new Error('지원하지 않는 백업 형식이에요.');
    const incoming = {};
    for (const table of DATA_TABLES) {
        const values = snapshot.tables[table] ?? [];
        if (!Array.isArray(values) || !values.every((row) => validBase(row) && validImportRow(table, row)) || new Set(values.map((row) => row.id)).size !== values.length)
            throw new Error('백업의 기록 형식이나 중복 ID를 확인해 주세요.');
        incoming[table] = values;
    }
    if (snapshot.attachments && (!Array.isArray(snapshot.attachments) || !snapshot.attachments.every((row) => validBase(row) && validImportRow('attachments', row)) || new Set(snapshot.attachments.map((row) => row.id)).size !== snapshot.attachments.length))
        throw new Error('첨부파일 정보나 중복 ID를 확인해 주세요.');
    incoming.attachments = await Promise.all((snapshot.attachments ?? []).map(attachmentFromBackup));
    if (snapshot.trash && (!Array.isArray(snapshot.trash) || !snapshot.trash.every(validTrash) || new Set(snapshot.trash.map((row) => row.id)).size !== snapshot.trash.length))
        throw new Error('휴지통 원본이나 중복 ID를 확인해 주세요.');
    incoming.trash = snapshot.trash ?? [];
    assertBackupAttachmentSize(incoming.attachments);
    return incoming;
}
function classifyRow(existing, incoming) {
    if (!existing || existing.updatedAt < incoming.updatedAt)
        return 'import';
    return existing.updatedAt === incoming.updatedAt ? 'compare' : 'unchanged';
}
export async function previewSnapshot(snapshot) {
    const incoming = await prepareSnapshot(snapshot);
    return db.transaction('r', storageTables.map(targetTable), async () => {
        const current = await readRawState();
        const projected = Object.fromEntries(storageTables.map((table) => [table, [...current[table]]]));
        const result = emptyResult();
        for (const table of storageTables)
            for (const row of incoming[table]) {
                const existing = current[table].find((item) => item.id === row.id);
                const kind = classifyRow(existing, row);
                if (kind === 'import') {
                    result.imported++;
                    projected[table] = [...projected[table].filter((item) => item.id !== row.id), row];
                }
                else if (kind === 'compare' && !await equalRow(existing, row))
                    result.conflicts++;
                else
                    result.unchanged++;
            }
        result.skipped = result.unchanged + result.conflicts;
        assertImportReferences(referenceState(projected), referenceState(incoming));
        return { result, expectedState: await stateKey(current) };
    });
}
export async function restoreLatestMigrationBackup() {
    const backup = await getLatestMigrationBackup();
    if (!backup)
        throw new Error('되돌릴 백업이 없어요.');
    if (!backup.changes) {
        // Legacy migration snapshots have no operation receipt. Restore absent rows
        // only; a newer living record is a conflict, never a silent no-op undo.
        const safeAttachments = [];
        const safeTrash = [];
        const safe = { ...backup.snapshot, tables: {}, attachments: safeAttachments, trash: safeTrash };
        const conflicts = emptyResult();
        for (const table of DATA_TABLES)
            for (const row of backup.snapshot.tables[table] ?? []) {
                const current = await targetTable(table).get(row.id);
                if (!current)
                    (safe.tables[table] ??= []).push(row);
                else if (!await equalRow(current, row))
                    conflicts.conflicts++;
            }
        for (const row of backup.snapshot.attachments ?? [])
            if (!await db.attachments.get(row.id))
                safeAttachments.push(row);
        for (const row of backup.snapshot.trash ?? []) {
            const current = await db.trash.get(row.id);
            if (!current)
                safeTrash.push(row);
            else if (!await equalRow(current, row))
                conflicts.conflicts++;
        }
        const result = await importSnapshot(safe, { remember: false });
        result.conflicts += conflicts.conflicts;
        result.skipped += conflicts.conflicts;
        return result;
    }
    return db.transaction('rw', [...storageTables.map(targetTable), db.migrationBackups], async () => {
        if (!await db.migrationBackups.get(backup.id))
            throw new Error('다른 복원 작업이 생겼어요. 다시 확인해 주세요.');
        const result = emptyResult();
        const retained = [];
        const raw = await readRawState();
        const trash = raw.trash;
        let eligible = [];
        const retain = (change, reason) => {
            result.conflicts++;
            retained.push(change);
            result.errors.push({ table: change.table, id: change.id, reason });
        };
        for (const change of backup.changes) {
            const current = raw[change.table].find((row) => row.id === change.id);
            if (await equalRow(current, change.after))
                eligible.push(change);
            else
                retain(change, '복원 뒤에 다시 수정된 기록은 유지했어요.');
        }
        // Undoing an imported parent must not orphan a later, independent child.
        // Re-evaluate until retained parents/children reach a stable reference set.
        let changed = true;
        while (changed) {
            changed = false;
            for (const change of [...eligible]) {
                if (change.before)
                    continue;
                const referenced = referenceTables.some((table) => raw[table].some((row) => {
                    const undo = eligible.find((item) => item.table === table && item.id === row.id);
                    const projected = undo ? undo.before : row;
                    if (!projected)
                        return false;
                    const value = projected;
                    return rowReferences(value, change.table, change.id);
                })) || trash.some((item) => {
                    const undo = eligible.find((changeItem) => changeItem.table === 'trash' && changeItem.id === item.id);
                    const projected = undo ? undo.before : item;
                    return !!projected?.snapshot && typeof projected.snapshot === 'object' && rowReferences(projected.snapshot, change.table, change.id);
                });
                if (referenced) {
                    eligible = eligible.filter((item) => item !== change);
                    retain(change, '나중에 추가된 기록이 연결되어 있어 원본을 유지했어요.');
                    changed = true;
                }
            }
        }
        for (const change of eligible) {
            const table = targetTable(change.table);
            if (change.before)
                await table.put({ ...change.before, updatedAt: Math.max(Date.now(), change.after.updatedAt + 1) });
            else
                await table.delete(change.id);
            result.imported++;
        }
        if (retained.length)
            await db.migrationBackups.put({ ...backup, changes: retained });
        else
            await db.migrationBackups.delete(backup.id);
        result.skipped = result.conflicts;
        return result;
    });
}
export async function importSnapshot(snapshot, options = {}) {
    const incoming = await prepareSnapshot(snapshot);
    return db.transaction('rw', [...storageTables.map(targetTable), db.migrationBackups], async () => {
        options.assertCurrent?.();
        const before = await readRawState();
        if (options.expectedState != null && await stateKey(before) !== options.expectedState)
            throw new Error('미리보기 뒤 기록이 바뀌었어요. 다시 확인해 주세요.');
        const result = emptyResult();
        for (const table of storageTables)
            for (const row of incoming[table]) {
                const existing = await targetTable(table).get(row.id);
                const kind = classifyRow(existing, row);
                if (kind === 'import') {
                    await targetTable(table).put(row);
                    result.imported++;
                }
                else if (kind === 'compare' && !await equalRow(existing, row)) {
                    result.conflicts++;
                    result.errors.push({ table, id: row.id, reason: '같은 수정 시각의 다른 내용은 현재 기록을 유지했어요.' });
                }
                else
                    result.unchanged++;
            }
        await collapseScheduledDuplicates();
        assertImportReferences(referenceState(await readRawState()), referenceState(incoming));
        assertBackupAttachmentSize(await db.attachments.toArray());
        if (options.remember !== false) {
            const after = await readRawState();
            const changes = [];
            for (const table of storageTables)
                for (const row of after[table]) {
                    const old = before[table].find((item) => item.id === row.id);
                    if (!await equalRow(old, row))
                        changes.push({ table, id: row.id, before: old ?? null, after: row });
                }
            if (changes.length) {
                const now = Date.now();
                const id = `import-${now}-${crypto.randomUUID()}`;
                await db.migrationBackups.put({ id, createdAt: now, snapshot: { schemaVersion: 1, exportedAt: now, tables: Object.fromEntries(DATA_TABLES.map((table) => [table, before[table]])), trash: before.trash }, changes });
                const old = await db.migrationBackups.toArray();
                await db.migrationBackups.bulkDelete(old.filter((item) => item.id !== id).map((item) => item.id));
            }
        }
        options.assertCurrent?.();
        result.skipped = result.unchanged + result.conflicts;
        return result;
    });
}
async function collapseScheduledDuplicates() {
    const treatmentGroups = new Map();
    const treatmentLogs = await db.treatmentLogs.toArray();
    for (const log of treatmentLogs) {
        if (typeof log.scheduledAt !== 'number')
            continue;
        const key = `${log.treatmentId}:${log.scheduledAt}`;
        const group = treatmentGroups.get(key) ?? [];
        group.push(log);
        treatmentGroups.set(key, group);
    }
    const treatmentUpdates = [...treatmentGroups.values()].flatMap((group) => {
        const ordered = [...group].sort((a, b) => a.recordedAt - b.recordedAt || a.id.localeCompare(b.id));
        const winner = ordered[0];
        if (!winner)
            return [];
        return [{ ...winner, duplicateOf: undefined }, ...ordered.slice(1).map((row) => ({ ...row, duplicateOf: winner.id }))];
    });
    if (treatmentUpdates.length)
        await db.treatmentLogs.bulkPut(treatmentUpdates);
    const routineGroups = new Map();
    const routineLogs = await db.routineLogs.toArray();
    for (const log of routineLogs) {
        if (typeof log.scheduledAt !== 'number')
            continue;
        const key = `${log.routineId}:${log.scheduledAt}`;
        const group = routineGroups.get(key) ?? [];
        group.push(log);
        routineGroups.set(key, group);
    }
    const routineUpdates = [...routineGroups.values()].flatMap((group) => {
        const ordered = [...group].sort((a, b) => a.createdAt - b.createdAt || a.id.localeCompare(b.id));
        const winner = ordered[0];
        if (!winner)
            return [];
        return [{ ...winner, duplicateOf: undefined }, ...ordered.slice(1).map((row) => ({ ...row, duplicateOf: winner.id }))];
    });
    if (routineUpdates.length)
        await db.routineLogs.bulkPut(routineUpdates);
}
export function snapshotToJson(snapshot) {
    return JSON.stringify(snapshot, null, 2);
}
export async function exportCsv() {
    const header = ['table', 'id', 'petId', 'recordedAt', 'date', 'type', 'value', 'note', 'data'];
    const rows = [header.map(csvCell).join(',')];
    for (const table of DATA_TABLES) {
        const records = await repository.list(table);
        for (const record of records) {
            const row = record;
            const fields = [table, row.id, row.petId, row.recordedAt ?? row.measuredAt ?? row.visitedAt ?? row.servedAt ?? row.occurredAt ?? row.filledAt ?? row.testedAt ?? row.startedAt, row.date, row.type, row.value, row.note, JSON.stringify(record)]
                .map((field) => csvCell(field));
            rows.push(fields.join(','));
        }
    }
    return `\ufeff${rows.join('\r\n')}`;
}
function csvCell(value) {
    const text = value == null ? '' : String(value);
    return `"${text.replaceAll('"', '""')}"`;
}
/** Bad CSV rows stay explicit. All readable records use the same atomic importer. */
export async function importCsv(text, now = Date.now()) {
    const parsed = parseCsvSnapshot(text, now);
    const result = await importSnapshot(parsed.snapshot);
    return { ...result, failed: parsed.errors.length, skipped: result.skipped + parsed.errors.length, errors: [...result.errors, ...parsed.errors.map((error) => ({ table: 'csv', id: String(error.row), reason: error.reason }))], rowErrors: parsed.errors };
}
export function parseCsvSnapshot(text, now = Date.now()) {
    const rows = parseCsv(text.replace(/^\ufeff/, ''));
    if (rows.length === 0)
        throw new Error('CSV가 비어 있어요.');
    if (rows.length > 20_001)
        throw new Error('CSV는 한 번에 20,000행까지 가져올 수 있어요.');
    const header = rows[0] ?? [];
    const dataIndex = header.indexOf('data');
    if (header[0] !== 'table' || dataIndex < 0)
        throw new Error('펫리듬에서 내보낸 CSV 형식이 아니에요.');
    const snapshot = { schemaVersion: 1, exportedAt: now, tables: {} };
    const errors = [];
    for (let index = 1; index < rows.length; index++) {
        const cells = rows[index] ?? [];
        const table = cells[0];
        try {
            if (!DATA_TABLES.includes(table))
                throw new Error('알 수 없는 기록 종류예요.');
            const row = JSON.parse(cells[dataIndex] ?? '');
            if (!validBase(row))
                throw new Error('필수 필드가 없어요.');
            (snapshot.tables[table] ??= []).push(row);
        }
        catch (error) {
            errors.push({ row: index + 1, reason: error instanceof Error ? error.message : '행을 읽지 못했어요.' });
        }
    }
    return { snapshot, errors };
}
function parseCsv(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let i = 0; i < text.length; i += 1) {
        const char = text[i];
        if (quoted) {
            if (char === '"' && text[i + 1] === '"') {
                cell += '"';
                i += 1;
            }
            else if (char === '"')
                quoted = false;
            else
                cell += char;
        }
        else if (char === '"' && cell === '')
            quoted = true;
        else if (char === ',') {
            row.push(cell);
            cell = '';
        }
        else if (char === '\n') {
            row.push(cell.replace(/\r$/, ''));
            rows.push(row);
            row = [];
            cell = '';
        }
        else
            cell += char;
    }
    if (cell.length || row.length) {
        row.push(cell);
        rows.push(row);
    }
    return rows.filter((candidate) => candidate.some((value) => value.length > 0));
}
